/**
 * Platform Permission Utilities
 *
 * Pure predicate functions for determining what actions the current user
 * is allowed to perform, based on the `profile.name` field returned by
 * `GET /auth/user-info`.
 *
 * These predicates are **framework-agnostic** (no React); prefer using
 * the `useUserPermissions` hook in components so that `useUserInfo` is
 * composed automatically.
 *
 * The backend returns the user's platform role in `profile.name`
 * (e.g. `"SUPERVISOR"`, `"Gestor"`). The raw `roles[]` array contains
 * auth-system roles (e.g. `ROLE_IDF_PLATAFORMAIDF_SUPERVISOR_USER`)
 * and must NOT be used here.
 *
 * Permission matrix:
 * | Type     | Profile        | View | Create Invite | Edit Users | Switch Clients        | Switch Entities |
 * |----------|----------------|------|---------------|------------|-----------------------|-----------------|
 * | Internal | Super Admin    |  ✓   |      ✓        |     ✓      | data-driven (>1 acct) |       ✓         |
 * | Internal | Viewer Avançado|  ✓   |      ✗        |     ✗      | data-driven (>1 acct) |       ✓         |
 * | Internal | Viewer Básico  |  ✓   |      ✗        |     ✗      | data-driven (>1 acct) |       ✓         |
 * | Internal | Comercial      |  ✓   |      ✓        |     ✓      | data-driven (>1 acct) |       ✗         |
 * | Internal | Contestação    |  ✗   |      ✗        |     ✗      | data-driven (>1 acct) |       ✗         |
 * | External | Advanced Admin |  ✓   |      ✓        |     ✓      | data-driven (>1 acct) |       ✓         |
 * | External | Basic Admin    |  ✓   |      ✓        |     ✓      | data-driven (>1 acct) |       ✓         |
 * | External | Gestor         |  ✓   |      ✓        |     ✓      | data-driven (>1 acct) |       ✓         |
 * | External | Supervisor     |  ✓   |      ✗        |     ✗      | data-driven (>1 acct) |       ✗         |
 * | External | Operator       |  ✗   |      ✗        |     ✗      | data-driven (>1 acct) |       ✗         |
 * | External | (others)       |  ✗   |      ✗        |     ✗      | data-driven (>1 acct) |       ✗         |
 *
 * @module shared/utils/permissions
 */

import { normalizeRole } from '@/shared/utils/normalizeRole';

// ─── Allowed sets ──────────────────────────────────────────────────────────

const CAN_VIEW = new Set([
  'COMERCIAL',
  'SUPER_ADMIN',
  'SUPERVISOR',
  'GESTOR',
  'BASIC_ADMIN',
  'ADVANCED_ADMIN',
]);

const CAN_CREATE_INVITE = new Set([
  'COMERCIAL',
  'SUPER_ADMIN',
  'GESTOR',
  'BASIC_ADMIN',
  'ADVANCED_ADMIN',
]);

const CAN_EDIT_USERS = new Set([
  'COMERCIAL',
  'SUPER_ADMIN',
  'GESTOR',
  'BASIC_ADMIN',
  'ADVANCED_ADMIN',
]);

const CAN_DELETE_USERS = new Set(['GESTOR', 'BASIC_ADMIN', 'ADVANCED_ADMIN']);

const CAN_SWITCH_ENTITIES = new Set([
  'SUPER_ADMIN',
  'ADVANCED_VIEWER',
  'BASIC_VIEWER',
  'ADVANCED_ADMIN',
  'BASIC_ADMIN',
  'GESTOR',
]);

const INTERNAL_PROFILES = new Set([
  'COMERCIAL',
  'SUPER_ADMIN',
  'ADVANCED_VIEWER',
  'BASIC_VIEWER',
  'CONTESTACAO',
]);

const CAN_ACCESS_USER_MANAGEMENT = new Set([
  'COMERCIAL',
  'SUPER_ADMIN',
  'ADVANCED_VIEWER',
  'BASIC_VIEWER',
  'SUPERVISOR',
  'GESTOR',
  'BASIC_ADMIN',
  'ADVANCED_ADMIN',
]);

// ─── Predicates ────────────────────────────────────────────────────────────

/**
 * Whether the user can VIEW the User Management screen.
 *
 * @param profileName - `profile.name` from `/auth/user-info` (case-insensitive)
 */
export const canViewUsers = (profileName: string | undefined): boolean => {
  if (!profileName) return false;
  return CAN_VIEW.has(normalizeRole(profileName));
};

/**
 * Whether the user can CREATE (send) invitations.
 *
 * Note: SUPERVISOR can view but NOT create invites.
 *
 * @param profileName - `profile.name` from `/auth/user-info` (case-insensitive)
 */
export const canCreateInvites = (profileName: string | undefined): boolean => {
  if (!profileName) return false;
  return CAN_CREATE_INVITE.has(normalizeRole(profileName));
};

/**
 * Whether the user can EDIT existing users (change profile / delete).
 *
 * Note: SUPERVISOR can view but NOT edit.
 *
 * @param profileName - `profile.name` from `/auth/user-info` (case-insensitive)
 */
export const canEditUsers = (profileName: string | undefined): boolean => {
  if (!profileName) return false;
  return CAN_EDIT_USERS.has(normalizeRole(profileName));
};

/**
 * Whether the user can DELETE existing users.
 *
 * Only external management roles can delete users.
 *
 * @param profileName - `profile.name` from `/auth/user-info` (case-insensitive)
 */
export const canDeleteUsers = (profileName: string | undefined): boolean => {
  if (!profileName) return false;
  return CAN_DELETE_USERS.has(normalizeRole(profileName));
};

/**
 * Whether the user can SWITCH between entities (platforms).
 *
 * Internal profiles with client access + external admin/gestor profiles.
 *
 * @param profileName - `profile.name` from `/auth/user-info` (case-insensitive)
 */
export const canSwitchEntities = (profileName: string | undefined): boolean => {
  if (!profileName) return false;
  return CAN_SWITCH_ENTITIES.has(normalizeRole(profileName));
};

/**
 * Whether the user can ACCESS the User Management screen.
 *
 * Excludes OPERATOR and CONTESTAÇÃO profiles.
 *
 * @param profileName - `profile.name` from `/auth/user-info` (case-insensitive)
 */
export const canAccessUserManagement = (
  profileName: string | undefined
): boolean => {
  if (!profileName) return false;
  return CAN_ACCESS_USER_MANAGEMENT.has(normalizeRole(profileName));
};

/**
 * Whether the profile is an INTERNAL profile type.
 *
 * @param profileName - `profile.name` from `/auth/user-info` (case-insensitive)
 */
export const isInternalProfile = (profileName: string | undefined): boolean => {
  if (!profileName) return false;
  return INTERNAL_PROFILES.has(normalizeRole(profileName));
};
